// People (prototype 60-people.js peoplePage(), MODALS.invite): seats, the people list, invitations
// with resend and cancel, and the invite form (invite_members). The person drawer comes in phase 8; until then each
// person's AI cheque reads (the drawer's box in the prototype) open from the AI reads column (company_ai_reads, set_member_ai_limit).
import { Money } from '@xr/ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { plain } from '../lib/errors';
import { roleName } from '../lib/names';
import { PLANS, type Seats, hasAdvRoles, seatCost } from '../lib/plans';
import { L, N } from '../lib/prefs';
import { go } from '../lib/route';
import { type Role, app, can, refreshSetup, useApp } from '../lib/session';
import { rpc, sb } from '../lib/supabase';
import { rel } from '../lib/time';
import { Avatar, Btn, Empty, Field, IBtn, Ic, Meter, Note, PageHead, Radios, Select } from '../ui/kit';
import { closeModal, openModal } from '../ui/modal';
import { soon } from '../shell/soon';
import { RoNote } from '../ui/settings';
import { FSel, Pill, RolePill, SearchBox, Table, Tabs } from '../ui/table';
import { toast } from '../ui/toast';

type Person = { membership_id: string; user_id: string; name: string; email: string; role: Role; seat_type: 'issuer' | 'office'; branch_id: string | null; status: string; last_active: string | null; mfa: boolean | null; deactivated_at: string | null };
type Invite = { id: string; email: string; name: string | null; role: Role; seat_type: 'issuer' | 'office'; invited_by: string; created_at: string; expires_at: string };
type Branch = { id: string; name_en: string; name_ar: string | null };
/** company_ai_reads(): own_limit null = the plan's, 0 = off; only issuer seats scan cheques. */
type AiRow = { membership_id: string; issuer: boolean; own_limit: number | null; plan_reads: number; reads_limit: number; used: number; reads_left: number; period: 'day' | 'week' | 'month'; scope: 'person' | 'company'; pool_left: number | null };

const ROLE_ORDER: Role[] = ['owner', 'admin', 'finance', 'supervisor', 'collector', 'auditor'];
const SEAT: Record<string, [string, string]> = { issuer: ['Issuer', 'مُصدر'], office: ['Office', 'مكتبي'] };
const ROLE_ABOUT: Record<string, [string, string]> = {
  admin: ['Manages people, rules and settings. Sees all money. Billing stays with the owner.', 'يدير الأشخاص والقواعد والإعدادات ويرى كل الأموال. الفوترة للمالك.'],
  finance: ['Confirms handovers, deposits cheques and cash, verifies transfers, approves voids. Can issue receipts on the web.', 'يؤكد التسليمات ويودع الشيكات والنقد ويتحقق من التحويلات ويوافق على الإلغاء. يمكنه إصدار السندات من الويب.'],
  supervisor: ['Leads a team of collectors: sees their receipts and cash, clears review flags.', 'يقود فريق محصلين: يرى سنداتهم ونقدهم ويراجع العلامات.'],
  collector: ['Issues receipts in the phone app and hands money to finance. No web access.', 'يصدر السندات من تطبيق الهاتف ويسلّم الأموال للمالية. لا يدخل الويب.'],
  auditor: ['Read-only access to everything, including the audit log. Can export.', 'قراءة فقط لكل شيء بما فيه سجل التدقيق. يمكنه التصدير.'],
};
const seatFor = (role: Role) => (role === 'collector' || role === 'supervisor' ? 'issuer' : role === 'auditor' ? 'office' : null);
/* AI cheque reads (prototype 60-people.js aiSection, MODALS.aiLimit). The admin sees the period; the phone never does. */
const AI_EVERY: Record<AiRow['period'], [string, string]> = { day: ['a day', 'يومياً'], week: ['a week', 'أسبوعياً'], month: ['a month', 'شهرياً'] };
const AI_WIN: Record<AiRow['period'], [string, string]> = { day: ['in the last 24 hours', 'في آخر 24 ساعة'], week: ['in the last 7 days', 'في آخر 7 أيام'], month: ['in the last month', 'في آخر شهر'] };
const nf0 = new Intl.NumberFormat('en-US');
type AiMode = 'plan' | 'own' | 'off';

function AiReads({ p, a, onSaved }: { p: Person; a: AiRow; onSaved: () => void }) {
  const plan = a.plan_reads;
  const adm = can('admin');
  const first = p.name.split(' ')[0] || p.name;
  const [mode, setMode] = useState<AiMode>(a.own_limit == null ? 'plan' : a.own_limit === 0 ? 'off' : 'own');
  const [n, setN] = useState(String(a.own_limit || plan));
  const [busy, setBusy] = useState(false);
  const num = Number(n);
  const err = mode === 'own' && !(Number.isInteger(num) && num >= 1 && num <= 100000) ? L('Use a whole number from 1 to 100,000.', 'استخدم رقماً صحيحاً من 1 إلى 100,000.') : '';
  const every = AI_EVERY[a.period], off = a.reads_limit === 0;
  const planTxt = L(`${nf0.format(plan)} ${every[0]}`, `${nf0.format(plan)} ${every[1]}`);
  const which = a.own_limit == null ? L(`Plan default: ${planTxt}`, `افتراضي الخطة: ${planTxt}`) : off ? L('Off. The phone still reads cheques itself.', 'متوقفة. يقرأ الهاتف الشيكات بنفسه.') : L(`Own limit: ${nf0.format(a.own_limit)} ${every[0]}`, `حد خاص: ${nf0.format(a.own_limit)} ${every[1]}`);
  const save = async () => {
    if (err) return;
    const next = mode === 'plan' ? null : mode === 'off' ? 0 : num;
    if (next === a.own_limit) { closeModal(); return; }
    setBusy(true);
    try {
      await rpc('set_member_ai_limit', { p_member: p.membership_id, p_limit: next });
      closeModal(); toast(L('Saved. ' + first + '\'s phone updates at the next scan.', 'تم. يتحدث هاتف ' + first + ' عند المسح التالي.')); onSaved();
    } catch (e) { toast(plain(e), { tone: 'bad' }); setBusy(false); }
  };
  return (
    <>
      <div className="m-body">
        <div className="ai-box">
          <div className="ai-top"><b>{which}</b></div>
          {off ? null : <>
            <div className="ai-use"><span>{L(`Used ${nf0.format(a.used)} of ${nf0.format(a.reads_limit)} ${AI_WIN[a.period][0]}`, `استُخدم ${nf0.format(a.used)} من ${nf0.format(a.reads_limit)} ${AI_WIN[a.period][1]}`)}</span><b>{L(nf0.format(a.reads_left) + ' left', 'المتبقي ' + nf0.format(a.reads_left))}</b></div>
            <Meter v={a.used} max={a.reads_limit} />
          </>}
          {a.scope === 'company' && a.pool_left != null ? <p className="muted sm">{L(`Your plan shares ${nf0.format(plan)} reads ${every[0]} across the company; ${nf0.format(a.pool_left)} are left. ${first}'s limit applies inside that pool.`, `تشارك خطتك ${nf0.format(plan)} قراءة ${every[1]} في الشركة؛ المتبقي ${nf0.format(a.pool_left)}.`)}</p> : null}
          <p className="muted sm">{L(`On the phone ${first} sees only the reads left.`, `على الهاتف يرى ${first} المتبقي فقط.`)}</p>
        </div>
        {adm ? <>
          <Radios<AiMode> name="ail" value={mode} onChange={setMode} opts={[
            ['plan', L('Plan default', 'افتراضي الخطة'), L(`${planTxt}, set by your plan`, `${nf0.format(plan)} ${every[1]} حسب خطتك`)],
            ['own', L('Own limit', 'حد خاص'), L('More or fewer than the plan gives', 'أكثر أو أقل مما تعطيه الخطة')],
            ['off', L('Off', 'إيقاف'), L('No AI reads. The phone still reads cheques itself.', 'لا قراءات ذكاء. يقرأ الهاتف الشيكات بنفسه.')]]} />
          {mode === 'own' ? (
            <Field label={L('Reads ', 'قراءات ') + L(every[0], every[1])} htmlFor="ail-n" err={err} hint={a.scope === 'company' ? L(`The company pool is ${nf0.format(plan)}; a person can't go past it.`, `رصيد الشركة ${nf0.format(plan)}؛ لا يتجاوزه الشخص.`) : ''}>
              <input id="ail-n" className={'inp' + (err ? ' bad' : '')} type="number" min={1} max={100000} value={n} onChange={e => setN(e.target.value)} />
            </Field>
          ) : null}
        </> : null}
      </div>
      <footer className="m-foot">
        {adm ? <>
          <Btn kind="ghost" onClick={closeModal}>{L('Cancel', 'إلغاء')}</Btn>
          <Btn kind="primary" busy={busy} disabled={!!err} why={err} onClick={() => void save()}>{L('Save', 'حفظ')}</Btn>
        </> : <Btn kind="primary" onClick={closeModal}>{L('Close', 'إغلاق')}</Btn>}
      </footer>
    </>
  );
}

const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const parse = (s: string) => [...new Set(s.split(/[\s,;]+/).map(x => x.trim().toLowerCase()).filter(Boolean))];

/** Everyone in the company. Uses company_people() when the database has it, else what RLS lets the panel read. */
async function loadPeople(tenant: string): Promise<Person[]> {
  const r = await sb.rpc('company_people');
  if (!r.error) return r.data as Person[];
  const [m, p] = await Promise.all([
    sb.from('memberships').select('id,user_id,role,seat_type,branch_id,status,work_email,deactivated_at').eq('tenant_id', tenant),
    sb.from('user_profiles').select('user_id,name'),
  ]);
  if (m.error) throw m.error;
  const names = new Map((p.data || []).map(x => [x.user_id, x.name]));
  return m.data.map(x => ({ membership_id: x.id, user_id: x.user_id, name: names.get(x.user_id) || x.work_email || '—', email: x.work_email || '', role: x.role, seat_type: x.seat_type, branch_id: x.branch_id, status: x.status, last_active: null, mfa: null, deactivated_at: x.deactivated_at }));
}

function InviteForm({ people, invites, branches, onSent }: { people: Person[]; invites: Invite[]; branches: Branch[]; onSent: (n: number) => void }) {
  const { co, me } = useApp();
  const [emails, setEmails] = useState('');
  const [role, setRole] = useState<Role>('collector');
  const [seat, setSeat] = useState<'issuer' | 'office'>('issuer');
  const [branch, setBranch] = useState(branches[0]?.id || '');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [serverErrs, setServerErrs] = useState<[string, string][]>([]);
  if (!co || !me) return null;
  const forced = seatFor(role);
  const theSeat = forced || seat;
  const list = parse(emails);
  const now = Date.now();
  const errs: [string, string][] = list.flatMap((e): [string, string][] =>
    !EMAIL.test(e) ? [[e, L('not a valid email', 'بريد غير صالح')]]
      : people.some(p => p.email === e && p.status === 'active') ? [[e, L('already a member', 'عضو بالفعل')]]
      : invites.some(i => i.email === e && Date.parse(i.expires_at) > now) ? [[e, L('already invited', 'مدعو بالفعل')]] : []);
  const shown = [...errs, ...serverErrs.filter(([e]) => list.includes(e) && !errs.some(x => x[0] === e))];
  const good = list.filter(e => !shown.some(x => x[0] === e));
  const pl = PLANS[co.plan];
  const seats: Seats = { issuer: people.filter(p => p.status === 'active' && p.seat_type === 'issuer').length, office: people.filter(p => p.status === 'active' && p.seat_type === 'office').length };
  const roles = ROLE_ORDER.filter(r => r !== 'owner' && (r !== 'admin' || ['owner', 'admin'].includes(me.role)) && (hasAdvRoles(co.plan) || !['supervisor', 'auditor'].includes(r)));
  const cost = good.length ? seatCost(co.plan, seats, theSeat, good.length) : 0;
  const send = async () => {
    setBusy(true);
    try {
      const res = await rpc<{ email: string; result: string }[]>('invite_members', { p_emails: good, p_role: role, p_seat: theSeat, p_branch: branch || null, p_message: msg.trim() || null });
      const bad = res.filter(r => r.result !== 'invited').map(r => [r.email, r.result === 'already_member' ? L('already a member', 'عضو بالفعل') : r.result === 'already_invited' ? L('already invited', 'مدعو بالفعل') : L('not a valid email', 'بريد غير صالح')] as [string, string]);
      const sent = res.length - bad.length;
      if (bad.length) setServerErrs(bad);
      if (sent) onSent(sent);
      if (!bad.length) closeModal();
    } catch (e) { toast(plain(e), { tone: 'bad' }); }
    finally { setBusy(false); }
  };
  return (
    <>
      <div className="m-body">
        <Field label={L('Email addresses', 'عناوين البريد')} htmlFor="inv-emails" hint={L('Paste several, separated by commas or new lines.', 'الصق عدة عناوين مفصولة بفواصل أو أسطر.')}>
          <textarea id="inv-emails" className="inp" rows={3} placeholder={'name@company.ae, …'} value={emails} onChange={e => { setEmails(e.target.value); setServerErrs([]); }} data-autofocus />
        </Field>
        {shown.length ? <ul className="err-list">{shown.map(([e, w]) => <li key={e}><Ic n="alert" cls="sm" /><b>{e}</b> {w}</li>)}</ul> : null}
        <div className="form-2">
          <Field label={L('Role', 'الدور')} htmlFor="inv-role" hint={hasAdvRoles(co.plan) ? '' : L('Supervisor and Auditor are on the Business plan.', 'المشرف والمدقق في خطة الأعمال.')}>
            <Select<Role> id="inv-role" value={role} opts={roles.map(r => [r, roleName(r)])} onChange={setRole} label={L('Role', 'الدور')} />
          </Field>
          <Field label={L('Seat', 'المقعد')} htmlFor="inv-seat" hint={forced ? L('Fixed for this role', 'ثابت لهذا الدور') : ''}>
            <Select<'issuer' | 'office'> id="inv-seat" value={theSeat} disabled={!!forced} onChange={setSeat} label={L('Seat', 'المقعد')} opts={[['issuer', L('Issuer · can issue receipts', 'مُصدر · يصدر السندات')], ['office', L('Office · cannot issue', 'مكتبي · لا يصدر')]]} />
          </Field>
          <Field label={L('Branch', 'الفرع')} htmlFor="inv-branch">
            <Select<string> id="inv-branch" value={branch} onChange={setBranch} label={L('Branch', 'الفرع')} opts={branches.map(b => [b.id, N(b.name_en, b.name_ar)])} />
          </Field>
        </div>
        <p className="muted sm"><Ic n="info" cls="sm" /> {L(ROLE_ABOUT[role]![0], ROLE_ABOUT[role]![1])}</p>
        <Field label={L('Personal note (optional)', 'رسالة شخصية (اختياري)')} htmlFor="inv-msg"><textarea id="inv-msg" className="inp" rows={2} value={msg} onChange={e => setMsg(e.target.value)} /></Field>
        {good.length && pl ? (
          <Note icon="card">{cost > 0
            ? <>{L(`Adds ${good.length} ${L(SEAT[theSeat]![0], SEAT[theSeat]![1]).toLowerCase()} seat${good.length > 1 ? 's' : ''}: about `, `يضيف ${good.length} مقعد: حوالي `)}<Money value={cost} decimals={0} />{L(' a month more, charged from the day each person accepts.', ' شهرياً إضافية من يوم القبول.')}</>
            : L(`Included in your ${L(pl.en, pl.ar)} plan. No extra charge.`, `مشمول في خطة ${L(pl.en, pl.ar)}. دون تكلفة إضافية.`)}</Note>
        ) : null}
      </div>
      <footer className="m-foot">
        <Btn kind="ghost" onClick={closeModal}>{L('Cancel', 'إلغاء')}</Btn>
        <Btn kind="primary" icon="send" busy={busy} disabled={!good.length} why={L('Add at least one valid email', 'أضف بريداً صالحاً')} onClick={() => void send()}>
          {good.length > 1 ? L(`Send ${good.length} invitations`, `إرسال ${good.length} دعوات`) : L('Send invitation', 'إرسال الدعوة')}
        </Btn>
      </footer>
    </>
  );
}

type Tab = 'active' | 'invited' | 'off';
export function PeoplePage() {
  const { co, me } = useApp();
  const [people, setPeople] = useState<Person[] | null>(null);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [names, setNames] = useState<Map<string, string>>(new Map());
  const [ai, setAi] = useState<Map<string, AiRow>>(new Map());
  const [failed, setFailed] = useState(false);
  const [tab, setTab] = useState<Tab>('active');
  const [q, setQ] = useState('');
  const [fRole, setFRole] = useState('');
  const [fMfa, setFMfa] = useState('');
  const id = co?.id;
  const reload = useCallback(async () => {
    if (!id) return;
    try {
      const [p, inv, br] = await Promise.all([
        loadPeople(id),
        can('admin') ? sb.from('invitations').select('id,email,name,role,seat_type,invited_by,created_at,expires_at').eq('tenant_id', id).is('accepted_at', null).is('cancelled_at', null).order('created_at', { ascending: false }) : Promise.resolve({ data: [], error: null }),
        sb.from('branches').select('id,name_en,name_ar').eq('tenant_id', id).order('is_head', { ascending: false }),
      ]);
      setPeople(p); setInvites((inv.data || []) as Invite[]); setBranches((br.data || []) as Branch[]);
      setNames(new Map(p.map(x => [x.user_id, x.name])));
      // AI reads are extra: the list still shows if they can't be read
      rpc<AiRow[]>('company_ai_reads').then(r => setAi(new Map((r || []).map(x => [x.membership_id, x])))).catch(() => setAi(new Map()));
    } catch { setFailed(true); }
  }, [id]);
  useEffect(() => { void reload(); }, [reload]);
  const adm = can('admin');
  const head = (
    <PageHead title={L('People', 'الأشخاص')} sub={L('Everyone who can sign in to ' + N(co?.name_en, co?.name_ar) + ', on the phone or here.', 'كل من يمكنه الدخول إلى ' + N(co?.name_en, co?.name_ar) + ' من الهاتف أو هنا.')}
      tools={people ? <>
        {can('seeAll') || me?.role === 'supervisor' ? <Btn kind="ghost" icon="download" onClick={() => soon(L('Export', 'تصدير'))}>{L('Export', 'تصدير')}</Btn> : null}
        {adm ? <Btn kind="primary" icon="userPlus" onClick={() => openInvite()}>{L('Invite people', 'دعوة أشخاص')}</Btn> : null}
      </> : null} />
  );
  const openInvite = () => openModal({
    title: L('Invite people', 'دعوة أشخاص'), sub: L('They get an email with a link. Collectors are sent straight to the phone app.', 'يصلهم بريد برابط. يوجه المحصلون مباشرة إلى تطبيق الهاتف.'), size: 'md', bare: true,
    body: <InviteForm people={people || []} invites={invites} branches={branches} onSent={n => {
      toast(L(`${n} invitation${n > 1 ? 's' : ''} sent`, `أُرسلت ${n} دعوات`), { icon: 'mail' });
      setTab('invited'); void reload(); void refreshSetup();
    }} />,
  });
  const list = useMemo(() => {
    const ql = q.toLowerCase().trim();
    return (people || []).filter(p => (tab === 'off' ? p.status === 'deactivated' : p.status === 'active') && (!ql || (p.name + ' ' + p.email).toLowerCase().includes(ql)) && (!fRole || p.role === fRole) && (!fMfa || (fMfa === 'on' ? p.mfa === true : fMfa === 'off' ? p.mfa === false : false)))
      .sort((a, b) => ROLE_ORDER.indexOf(a.role) * 100 + a.name.charCodeAt(0) - (ROLE_ORDER.indexOf(b.role) * 100 + b.name.charCodeAt(0)));
  }, [people, tab, q, fRole, fMfa]);
  if (failed) return <>{head}<div className="card"><Empty icon="alert" title={L('Something went wrong on this page', 'حدث خطأ في هذه الصفحة')} body={plain(null)} /></div></>;
  if (!people || !co || !me) return head;

  const act = people.filter(p => p.status === 'active'), off = people.filter(p => p.status === 'deactivated');
  const iss = act.filter(p => p.seat_type === 'issuer').length, ofc = act.filter(p => p.seat_type === 'office').length;
  const pl = PLANS[co.plan];
  const now = Date.now();
  const resend = async (i: Invite) => {
    try { await rpc('resend_invitation', { p_invitation: i.id }); toast(L('Invitation sent again to ' + i.email, 'أعيد إرسال الدعوة إلى ' + i.email), { icon: 'mail' }); void reload(); }
    catch (e) { toast(plain(e), { tone: 'bad' }); }
  };
  const cancel = async (i: Invite) => {
    try { await rpc('cancel_invitation', { p_invitation: i.id }); toast(L('Invitation cancelled', 'أُلغيت الدعوة')); void reload(); void refreshSetup(); }
    catch (e) { toast(plain(e), { tone: 'bad' }); }
  };

  const openAi = (p: Person, a: AiRow) => openModal({ title: L('AI cheque reads for ', 'قراءات الذكاء لـ ') + p.name, size: 'sm', bare: true, body: <AiReads p={p} a={a} onSaved={() => void reload()} /> });
  const aiCell = (p: Person) => {
    const a = ai.get(p.membership_id);
    if (tab === 'off' || !a || !a.issuer) return <span className="muted">—</span>;
    return (
      <button type="button" className="ai-cell" onClick={() => openAi(p, a)} aria-label={L('AI cheque reads for ', 'قراءات الذكاء لـ ') + p.name}>
        {a.reads_limit === 0 ? <Pill tone="mute">{L('Off', 'متوقف')}</Pill> : <span className="mono">{nf0.format(a.used)} / {nf0.format(a.reads_limit)}</span>}
        {a.own_limit != null && a.own_limit !== 0 ? <small className="sub">{L('own limit', 'حد خاص')}</small> : null}
      </button>
    );
  };

  return (
    <>
      {head}
      <RoNote />
      {pl ? (
        <div className="seats">
          <div><span>{L('Issuer seats', 'مقاعد المُصدرين')}</span><b>{iss}</b><small>{L('can issue receipts', 'يمكنهم إصدار السندات')} · {L(pl.incIss + ' included, then ', pl.incIss + ' مشمول ثم ')}<Money value={pl.issuer} decimals={0} />/{L('mo', 'شهر')}</small></div>
          <div><span>{L('Office seats', 'المقاعد المكتبية')}</span><b>{ofc}</b><small>{L('finance, admin, audit', 'المالية والإدارة والتدقيق')} · {pl.incOff ? L(pl.incOff + ' included, then ', pl.incOff + ' مشمول ثم ') : ''}<Money value={pl.office} decimals={0} />/{L('mo', 'شهر')}</small></div>
          <div><span>{L('Pending invites', 'دعوات معلقة')}</span><b>{invites.filter(i => Date.parse(i.expires_at) > now).length}</b><small>{L('not charged until accepted', 'لا تحتسب حتى القبول')}</small></div>
          {can('billing') ? <button type="button" className="link" onClick={() => go('billing')}>{L('Billing', 'الفوترة')} <Ic n={document.documentElement.dir === 'rtl' ? 'chevL' : 'chevR'} cls="sm" /></button> : null}
        </div>
      ) : null}
      <Tabs<Tab> items={[['active', L('Active', 'نشط'), act.length], ['invited', L('Invited', 'مدعو'), invites.length], ['off', L('Deactivated', 'معطّل'), off.length]]} cur={tab} onPick={setTab} />
      {tab === 'invited' ? (
        <div className="card flush">
          <Table<Invite> rows={invites} rowKey={i => i.id}
            empty={<Empty icon="mail" title={L('No pending invitations', 'لا دعوات معلقة')} action={adm ? <Btn kind="primary" icon="userPlus" onClick={openInvite}>{L('Invite people', 'دعوة أشخاص')}</Btn> : null} />}
            cols={[
              { k: 'n', label: L('Person', 'الشخص'), cls: 'grow', html: i => <><b>{i.name || i.email}</b><small className="sub">{i.email}</small></> },
              { k: 'r', label: L('Role', 'الدور'), html: i => <RolePill r={i.role} /> },
              { k: 's', label: L('Seat', 'المقعد'), cls: 'hide-sm', html: i => L(SEAT[i.seat_type]![0], SEAT[i.seat_type]![1]) },
              { k: 'by', label: L('Invited by', 'دعاه'), cls: 'hide-md', html: i => <><bdi>{names.get(i.invited_by) || '—'}</bdi><small className="sub">{rel(i.created_at)}</small></> },
              { k: 'st', label: L('Status', 'الحالة'), html: i => Date.parse(i.expires_at) < now ? <Pill tone="bad">{L('Expired', 'منتهية')}</Pill> : <Pill tone="warn">{L('Expires ', 'تنتهي ') + rel(i.expires_at)}</Pill> },
              { k: 'a', label: '', html: i => adm ? <span className="row-act"><Btn sm kind="ghost" onClick={() => void resend(i)}>{L('Resend', 'إعادة')}</Btn><IBtn icon="trash" title={L('Cancel invite', 'إلغاء الدعوة')} onClick={() => void cancel(i)} /></span> : null },
            ]} />
        </div>
      ) : (
        <>
          <div className="toolbar">
            <SearchBox id="people" value={q} onChange={setQ} ph={L('Name, email or phone', 'الاسم أو البريد أو الهاتف')} />
            <FSel id="f-people-role" label={L('Role', 'الدور')} value={fRole} onChange={setFRole} opts={ROLE_ORDER.map(r => [r, roleName(r)])} />
            <FSel id="f-people-mfa" label={L('Two-step', 'التحقق بخطوتين')} value={fMfa} onChange={setFMfa} opts={[['on', L('On', 'مفعل')], ['missing', L('Required, not set up', 'مطلوب وغير مفعل')], ['off', L('Off', 'متوقف')]]} />
          </div>
          <div className="card flush">
            <Table<Person> rows={list} rowKey={p => p.membership_id} empty={<Empty icon="users" title={L('Nobody here', 'لا أحد هنا')} />}
              cols={[
                { k: 'n', label: L('Person', 'الشخص'), cls: 'grow', sort: p => p.name, html: p => <span className="who"><Avatar name={p.name} role={p.role} /><span><b>{p.name}{p.user_id === me.id ? <> <small className="you">{L('you', 'أنت')}</small></> : null}</b><small className="sub">{p.email}</small></span></span> },
                { k: 'r', label: L('Role', 'الدور'), sort: p => ROLE_ORDER.indexOf(p.role), html: p => <RolePill r={p.role} /> },
                { k: 's', label: L('Seat', 'المقعد'), cls: 'hide-sm', html: p => <span className={'seat s-' + p.seat_type}>{L(SEAT[p.seat_type]![0], SEAT[p.seat_type]![1])}</span> },
                { k: 'b', label: L('Branch · team', 'الفرع · الفريق'), cls: 'hide-lg', html: p => { const b = branches.find(x => x.id === p.branch_id) || branches[0]; return b ? N(b.name_en, b.name_ar) : '—'; } },
                { k: 'm', label: L('Two-step', 'خطوتان'), cls: 'hide-md', html: p => p.mfa == null ? <span className="muted">—</span> : p.mfa ? <span className="ok-t"><Ic n="shield" cls="sm" /> {L('On', 'مفعل')}</span> : <span className="muted">{L('Off', 'متوقف')}</span> },
                { k: 'a', label: tab === 'off' ? L('Deactivated', 'عُطّل') : L('Last active', 'آخر نشاط'), sort: p => (tab === 'off' ? p.deactivated_at : p.last_active) || '', html: p => tab === 'off' ? (p.deactivated_at ? rel(p.deactivated_at) : '—') : p.last_active ? rel(p.last_active) : <span className="muted">—</span> },
                { k: 'ai', label: L('AI reads', 'قراءات الذكاء'), num: true, sort: p => ai.get(p.membership_id)?.used ?? -1, html: p => aiCell(p) },
                { k: 'h', label: L('Holding', 'يحمل'), num: true, cls: 'hide-sm', html: () => <span className="muted">—</span> },
              ]} />
          </div>
        </>
      )}
    </>
  );
}
